import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { FileText, Plus, ArrowDown, Pencil, Trash } from "@phosphor-icons/react";

export default function TemplatePicker({ category = "disclaimer", currentValue, onInsert }) {
  const [templates, setTemplates] = useState([]);
  const [showManager, setShowManager] = useState(false);
  const [newName, setNewName] = useState("");
  const [newText, setNewText] = useState("");

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/reference/templates`);
      setTemplates(data.filter(t => t.category === category));
    } catch {}
  };

  useEffect(() => { fetchTemplates(); }, [category]);

  const handleSaveNew = async () => {
    if (!newName.trim() || !newText.trim()) {
      toast.error("Name and text are required");
      return;
    }
    try {
      await axios.post(`${API}/reference/templates`, {
        name: newName, text: newText, category
      });
      toast.success("Template saved");
      setNewName("");
      setNewText("");
      fetchTemplates();
    } catch {
      toast.error("Failed to save template");
    }
  };

  const handleSaveCurrent = async () => {
    if (!currentValue?.trim()) {
      toast.error("No text to save as template");
      return;
    }
    const name = prompt("Template name:");
    if (!name?.trim()) return;
    try {
      await axios.post(`${API}/reference/templates`, {
        name: name.trim(), text: currentValue, category
      });
      toast.success("Saved as template");
      fetchTemplates();
    } catch {
      toast.error("Failed to save");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/reference/templates/${id}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const label = category === "disclaimer" ? "Disclaimer" : "Remarks";

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="template-picker">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {label} Templates
        </h3>
        <div className="flex gap-2">
          {currentValue?.trim() && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveCurrent}
              className="text-xs gap-1"
              data-testid="save-as-template-btn"
            >
              <Plus size={12} /> Save current as template
            </Button>
          )}
          <Dialog open={showManager} onOpenChange={setShowManager}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs gap-1" data-testid="manage-templates-btn">
                <Pencil size={12} /> Manage
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="template-manager-dialog">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Manage {label} Templates</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {/* Existing templates */}
                {templates.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {templates.map(t => (
                      <div key={t.id} className="flex items-start gap-2 p-2 border border-slate-200 rounded text-sm">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{t.name}</p>
                          <p className="text-slate-500 text-xs line-clamp-2">{t.text}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(t.id)}
                          className="text-red-500 hover:bg-red-50 h-7 w-7 p-0 flex-shrink-0"
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Add new */}
                <div className="border-t border-slate-200 pt-3">
                  <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">Add New Template</Label>
                  <Input
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="Template name"
                    className="mb-2 text-sm border-slate-300"
                  />
                  <Textarea
                    value={newText}
                    onChange={e => setNewText(e.target.value)}
                    placeholder="Template text..."
                    className="mb-2 text-sm border-slate-300"
                    rows={3}
                  />
                  <Button onClick={handleSaveNew} size="sm" className="bg-blue-900 hover:bg-blue-800 text-white gap-1">
                    <Plus size={14} /> Save Template
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Quick insert buttons */}
      {templates.length > 0 ? (
        <div className="flex gap-2 flex-wrap">
          {templates.map(t => (
            <Button
              key={t.id}
              variant="outline"
              size="sm"
              onClick={() => onInsert(t.text)}
              className="text-xs gap-1 h-7"
              title={t.text}
              data-testid={`template-insert-${t.id}`}
            >
              <ArrowDown size={12} /> {t.name}
            </Button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400">No templates saved yet. Save your current {label.toLowerCase()} or create new ones via Manage.</p>
      )}
    </div>
  );
}
